import {
  IChecklistItemPopulated,
  IChecklistItemRow,
  IStampSettings,
  onChecklistRowChange,
} from "@mctypes/checklist.types";
import { IMinUserDepricated, Paths, PathValue } from "@mctypes/global.types";
import { Card, Grid, Stack, Text, Title } from "@mantine/core";
import React from "react";
import { McTitle } from "../../../../../components";
import FoldableSection from "../../../../../components/FoldableSection/FoldableSection";
import { LinkifiedText } from "../../../../../components/LinkifiedText";
import McInputNew from "../../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../../components/McInput/McTextAreaNew";
import { setShowSuccessMessageThunk } from "../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { AppModalEnums } from "../../../../../mc-shared/sharedConstants";
import { updateChecklistCategoryThunk, updateChecklistItemThunk } from "../../checklistSlice";
import ChecklistComments from "./ChecklistComments";
import ChecklistConnecetedIssues from "./ChecklistConnecetedIssues";
import ChecklistDetailsRowChecksSection from "./ChecklistDetailsRowChecksSection";

const ChecklistDetails: React.FC<{
  checklistRow: IChecklistItemRow;
  isItem: boolean;
  isTemplate: boolean;
  onAssignedToChange: <T extends keyof IChecklistItemRow>(
    path: string,
    newValue: IChecklistItemRow[T],
    oldValue: string,
  ) => void;
  editable: boolean;
  updateRowAttribute: <P extends Paths<IChecklistItemPopulated, 4>>(
    rowId: string,
    path: P,
    oldValue: string,
    newValue: PathValue<IChecklistItemPopulated, P>,
  ) => void;
  onOpenIssue: (props: { itemId: string; boardId: string; app: AppModalEnums }) => void;
  stampSettings: IStampSettings;
}> = ({ checklistRow, onAssignedToChange, updateRowAttribute, isItem, isTemplate, editable, stampSettings }) => {
  const project = useAppSelector((state) => state.adminReducer.project);

  const checklist = useAppSelector((state) => state.checklistReducer.checklist);
  const dispatch = useAppDispatch();

  const memberUsers = (project.members ?? [])
    .map((member) => member.user)
    .filter((user): user is IMinUserDepricated => typeof user !== "string" && user != null);

  const updateRowParameter: onChecklistRowChange = async (oldValue, newValue, parameter) => {
    if (oldValue === newValue) {
      return;
    }

    if (isItem === true) {
      dispatch(updateChecklistItemThunk(checklistRow._id, parameter, newValue, oldValue?.toString()));
      dispatch(setShowSuccessMessageThunk("Sjekklisterad oppdatert"));
    } else {
      dispatch(updateChecklistCategoryThunk(checklistRow._id, parameter, newValue, oldValue?.toString()));
      dispatch(setShowSuccessMessageThunk("Sjekklistekategori oppdatert"));
    }
  };

  const referenceValue = typeof checklistRow.reference === "string" ? checklistRow.reference.trim() : "";
  const hasConnectedIssues = (checklistRow.connectedIssues ?? []).some((issue) => issue.archived !== true);

  return (
    <Stack gap="sm">
      <FoldableSection storageKey="checklist-modal-details">
        <FoldableSection.Header>
          <Title order={6}>Detaljer</Title>
        </FoldableSection.Header>

        <FoldableSection.Body>
          <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
            <Stack gap="sm">
              <Grid>
                <Grid.Col span={12}>
                  <McTitle title="Tittel">
                    {editable === true ? (
                      <Stack gap={6}>
                        {isItem === true ? (
                          <McTextAreaNew
                            required={true}
                            autosize
                            onBlur={(e) => updateRowParameter(checklistRow.title, e.target.value, "title")}
                            value={checklistRow.title}
                          />
                        ) : (
                          <McInputNew
                            required={true}
                            onBlur={(e) => updateRowParameter(checklistRow.title, e.target.value, "title")}
                            value={checklistRow.title}
                          />
                        )}

                        {isTemplate !== true && (
                          <McInputNew
                            required={false}
                            placeholder="Referanse"
                            onBlur={(e) => updateRowParameter(checklistRow.reference, e.target.value, "reference")}
                            value={checklistRow.reference ?? ""}
                          />
                        )}
                      </Stack>
                    ) : (
                      <Stack gap={2}>
                        <Text size="sm">
                          <LinkifiedText text={checklistRow.title} />
                        </Text>
                        {isTemplate !== true && referenceValue.length > 0 && (
                          <Text size="xs" c="dimmed">
                            <LinkifiedText text={referenceValue} />
                          </Text>
                        )}
                      </Stack>
                    )}
                  </McTitle>
                </Grid.Col>
              </Grid>

              <ChecklistDetailsRowChecksSection
                updateRowAttribute={updateRowAttribute}
                onAssignedToChange={onAssignedToChange}
                checkTypes={checklist.checkTypes}
                users={memberUsers}
                isItem={isItem}
                isTemplate={isTemplate}
                checklistRow={checklistRow}
                editable={editable}
                onValueChange={updateRowParameter}
                stampSettings={stampSettings}
              />
            </Stack>
          </Card>
        </FoldableSection.Body>
      </FoldableSection>

      {isTemplate === false && isItem === true && hasConnectedIssues === true && (
        <ChecklistConnecetedIssues checklistRow={checklistRow} projectId={project._id} />
      )}
      {isTemplate === false && isItem === true && (
        <ChecklistComments comments={checklistRow.comments ?? []} checklistItemId={checklistRow._id} />
      )}
    </Stack>
  );
};

export default ChecklistDetails;
